import { getJson } from '../services/api.js';
import { getLanguage } from '../services/i18n.js';
import { element, showMessage } from '../components/publicUi.js';

const validId = id => /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(id);
const captureDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value ?? '')
  ? `Дата съёмки: ${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}` : 'Дата съёмки не указана';
export class GalleryPage {
  constructor(onNavigate, request = getJson, initialRoute = '') {
    this.onNavigate = onNavigate; this.request = request; this.page = 1; this.sequence = 0; this.viewerSequence = 0;
    const [slug, query = ''] = String(initialRoute).split('?');
    this.sectionSlug = slug || null; this.searchQuery = (new URLSearchParams(query).get('q') || '').slice(0, 200);
  }
  render() {
    const root = element('section', 'container page-wrapper');
    const header = element('div', 'page-header'); this.breadcrumb = element('nav', 'page-breadcrumb file-breadcrumb');
    this.breadcrumb.setAttribute('aria-label', 'Путь по разделам галереи');
    header.append(this.breadcrumb, element('h1', 'page-title', '>_ ./gallery.raw'),
      element('p', 'page-desc', 'Изображения, отладочные сессии и аппаратные стенды. Выберите раздел или найдите фотографию.'));
    const layout = element('div', 'file-catalog-layout gallery-catalog-layout');
    this.menu = element('details', 'file-section-menu gallery-section-menu');
    this.menu.open = !(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 760px)').matches);
    this.menu.append(element('summary', 'file-menu-title', 'Разделы галереи'));
    this.sectionTree = element('nav'); this.sectionTree.setAttribute('aria-label', 'Разделы галереи'); this.menu.append(this.sectionTree);
    const main = element('div', 'file-catalog-main');
    const filters = element('form', 'category-filter-bar');
    this.search = element('input', 'auth-input'); this.search.type = 'search'; this.search.maxLength = 200; this.search.placeholder = 'Поиск изображений'; this.search.setAttribute('aria-label', 'Поиск изображений'); this.search.value = this.searchQuery;
    const find = element('button', 'filter-btn', 'Найти'); find.type = 'submit';
    filters.append(this.search, find);
    filters.addEventListener('submit', event => { event.preventDefault(); this.searchQuery = this.search.value; this.page = 1; this.updateHash(); void this.load(); });
    this.content = element('div'); this.pager = element('nav', 'blog-pagination'); this.pager.setAttribute('aria-label', 'Страницы галереи');
    main.append(filters, this.content, this.pager); layout.append(this.menu, main); root.append(header, layout); void this.initialize(); return root;
  }
  async initialize() {
    this.sectionController?.abort(); this.sectionController = new AbortController(); const { signal } = this.sectionController;
    showMessage(this.content, 'Загрузка разделов…');
    try {
      const result = await this.request(`/gallery-sections?lang=${getLanguage()}`, { signal });
      if (signal.aborted || this.disposed) return;
      this.sections = Array.isArray(result.data) ? result.data : [];
      this.renderSections(); this.renderBreadcrumb(); await this.load();
    } catch (error) { if (!signal.aborted && !this.disposed) showMessage(this.content, error.message, () => this.initialize()); }
  }
  routeSuffix() {
    const query = new URLSearchParams(); if (this.searchQuery) query.set('q', this.searchQuery);
    return `${this.sectionSlug || ''}${query.size ? `?${query}` : ''}`;
  }
  updateHash() {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const suffix = this.routeSuffix(); window.history.replaceState(null, '', suffix ? `#gallery/${suffix}` : '#gallery');
  }
  selectSection(slug) {
    if (slug === this.sectionSlug || this.disposed) return;
    this.sectionSlug = slug; this.page = 1;
    if (this.onNavigate) this.onNavigate('gallery', this.routeSuffix());
    else { this.updateHash(); this.renderSections(); this.renderBreadcrumb(); void this.load(); }
  }
  renderSections() {
    this.sectionTree.replaceChildren();
    const root = element('ul', 'file-section-tree');
    const all = element('button', `file-section-link${this.sectionSlug ? '' : ' active'}`, 'Все изображения');
    all.type = 'button'; all.setAttribute('aria-current', this.sectionSlug ? 'false' : 'page');
    all.addEventListener('click', () => this.selectSection(null));
    const allItem = element('li'); allItem.append(all); root.append(allItem);
    const byParent = new Map();
    for (const section of this.sections || []) {
      const key = section.parentId || null; if (!byParent.has(key)) byParent.set(key, []); byParent.get(key).push(section);
    }
    const visited = new Set();
    const appendChildren = (parentId, target) => {
      for (const section of byParent.get(parentId) || []) {
        if (visited.has(section.id)) continue; visited.add(section.id);
        const item = element('li'), button = element('button', `file-section-link${section.slug === this.sectionSlug ? ' active' : ''}`, section.name);
        button.type = 'button'; button.setAttribute('aria-current', section.slug === this.sectionSlug ? 'page' : 'false');
        button.addEventListener('click', () => this.selectSection(section.slug)); item.append(button);
        if (byParent.has(section.id)) { const children = element('ul', 'file-section-tree'); appendChildren(section.id, children); item.append(children); }
        target.append(item);
      }
    };
    appendChildren(null, root); this.sectionTree.append(root);
  }
  renderBreadcrumb() {
    this.breadcrumb.replaceChildren();
    const home = element('button', 'file-breadcrumb-link', 'Галерея'); home.type = 'button';
    home.setAttribute('aria-current', this.sectionSlug ? 'false' : 'page');
    home.addEventListener('click', () => this.selectSection(null)); this.breadcrumb.append(home);
    if (!this.sectionSlug) return;
    const byId = new Map((this.sections || []).map(section => [section.id, section]));
    const path = [], seen = new Set(); let current = (this.sections || []).find(section => section.slug === this.sectionSlug);
    while (current && !seen.has(current.id)) { seen.add(current.id); path.unshift(current); current = current.parentId ? byId.get(current.parentId) : null; }
    if (!path.length) { this.breadcrumb.append(element('span', '', ' / Раздел недоступен')); return; }
    for (const section of path) {
      this.breadcrumb.append(element('span', '', ' / '));
      const link = element('button', 'file-breadcrumb-link', section.name); link.type = 'button';
      link.setAttribute('aria-current', section.slug === this.sectionSlug ? 'page' : 'false');
      link.addEventListener('click', () => this.selectSection(section.slug)); this.breadcrumb.append(link);
    }
  }
  async load() {
    if (this.disposed) return;
    this.closeModal(); this.controller?.abort(); this.controller = new AbortController();
    const { signal } = this.controller, sequence = ++this.sequence;
    showMessage(this.content, 'Загрузка галереи…'); this.pager.replaceChildren();
    const query = new URLSearchParams({ page: String(this.page), limit: '12' });
    query.set('lang', getLanguage());
    if (this.searchQuery) query.set('q', this.searchQuery);
    if (this.sectionSlug) query.set('section', this.sectionSlug);
    try {
      const result = await this.request(`/gallery?${query}`, { signal });
      if (signal.aborted || this.disposed || sequence !== this.sequence) return;
      this.content.replaceChildren();
      if (!result.data.length) showMessage(this.content, 'Изображения не найдены.');
      else {
        const grid = element('div', 'gallery-grid');
        for (const item of result.data) {
          const card = element('button', 'gallery-card'); card.type = 'button'; card.setAttribute('aria-label', `Открыть: ${item.title}`);
          const thumb = element('div', 'gallery-thumb'), info = element('div', 'gallery-info');
          if (validId(item.id)) {
            const image = element('img', 'gallery-preview-art'); image.alt = item.altText || item.title; image.loading = 'lazy'; image.decoding = 'async';
            image.addEventListener('error', () => { if (!this.disposed) thumb.replaceChildren(element('span', '', 'Превью недоступно')); }, { once: true });
            image.src = `/api/v1/gallery/${item.id}/media/preview`; thumb.append(image);
            card.addEventListener('click', () => this.openModal(item, card));
          } else { thumb.append(element('span', '', 'Изображение недоступно')); card.disabled = true; }
          const title = element('div', 'gallery-title', item.title);
          if (/^#[0-9a-f]{6}$/i.test(item.color)) title.style.color = item.color;
          info.append(element('div', 'gallery-desc', [item.section?.name ?? item.category, item.type].filter(Boolean).join(' · ')),
            title, element('div', 'gallery-desc', item.description), element('div', 'gallery-desc', captureDate(item.takenOn)));
          card.append(thumb, info); grid.append(card);
        }
        this.content.append(grid);
      }
      const { page, total, totalPages } = result.meta;
      const prev = element('button', 'filter-btn', '← Назад'), next = element('button', 'filter-btn', 'Вперёд →');
      prev.disabled = page <= 1; next.disabled = page >= totalPages;
      prev.addEventListener('click', () => { this.page--; void this.load(); }); next.addEventListener('click', () => { this.page++; void this.load(); });
      this.pager.append(prev, element('span', '', `Страница ${page} из ${Math.max(1, totalPages)} · Всего: ${total}`), next);
    } catch (error) { if (!signal.aborted && !this.disposed && sequence === this.sequence) showMessage(this.content, error.message, () => this.load()); }
  }
  openModal(item, opener) {
    if (this.disposed || !validId(item.id)) return;
    this.closeModal(); this.opener = opener;
    const dialog = element('dialog', 'gallery-dialog'); this.modal = dialog; dialog.setAttribute('aria-label', item.title);
    const header = element('div', 'terminal-header'), close = element('button', 'modal-close-red', '[ Закрыть ✕ ]'); close.type = 'button';
    header.append(element('span', 'terminal-title', item.title), close);
    const body = element('div', 'modal-body'), media = element('div');
    const image = element('img', 'gallery-full-image'); image.alt = item.altText || item.title;
    image.addEventListener('error', () => { if (this.modal === dialog) media.replaceChildren(element('p', 'public-message', 'Изображение недоступно. Возможно, оно снято с публикации.')); }, { once: true });
    // A fresh URL also avoids reusing a decoded image from a previously closed viewer.
    image.src = `/api/v1/gallery/${item.id}/media/original?view=${Date.now()}-${++this.viewerSequence}`; media.append(image);
    body.append(media, element('p', 'profile-text', captureDate(item.takenOn)), element('p', 'profile-text', item.description)); dialog.append(header, body); document.body.append(dialog);
    close.addEventListener('click', () => this.closeModal());
    dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeModal(); });
    dialog.addEventListener('click', event => { if (event.target === dialog) this.closeModal(); });
    dialog.showModal(); close.focus();
  }
  closeModal() { if (!this.modal) return; this.modal.close(); this.modal.remove(); this.modal = null; if (!this.disposed) this.opener?.focus(); this.opener = null; }
  dispose() { this.disposed = true; this.sectionController?.abort(); this.controller?.abort(); this.closeModal(); this.content?.replaceChildren(); }
}
